<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { useModalConfirmationStore } from '@/stores/modal/confirmation'

const modalConfirmationStore = useModalConfirmationStore()
const panel = ref<HTMLElement | null>(null)

const isOpen = computed(() => modalConfirmationStore.isOpenModal)
const title = computed(() => modalConfirmationStore.title)
const message = computed(() => modalConfirmationStore.message)
const textCancel = computed(() => modalConfirmationStore.textCancel)
const textConfirm = computed(() => modalConfirmationStore.textConfirm)
const typeClass = computed(() => `is-${modalConfirmationStore.type || 'danger'}`)

watch(isOpen, async (open) => {
    if (!open) return
    await nextTick()
    panel.value?.focus()
})
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="beta-confirmation-modal"
            @keydown.esc="modalConfirmationStore.handleCancel()"
        >
            <button
                type="button"
                class="beta-confirmation-modal__backdrop"
                aria-label="Cancelar"
                @click="modalConfirmationStore.handleCancel()"
            ></button>

            <section
                ref="panel"
                class="beta-confirmation-modal__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="beta-confirmation-modal-title"
                tabindex="-1"
            >
                <header>
                    <h2 id="beta-confirmation-modal-title">{{ title }}</h2>
                </header>

                <div class="beta-confirmation-modal__body" v-html="message"></div>

                <footer>
                    <button
                        type="button"
                        class="is-cancel"
                        @click="modalConfirmationStore.handleCancel()"
                    >
                        {{ textCancel }}
                    </button>
                    <button
                        type="button"
                        :class="['is-confirm', typeClass]"
                        @click="modalConfirmationStore.handleConfirm()"
                    >
                        {{ textConfirm }}
                    </button>
                </footer>
            </section>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-confirmation-modal {
    position: fixed;
    inset: 0;
    z-index: 300;
    display: grid;
    place-items: center;
    padding: 16px;
}

.beta-confirmation-modal__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
}

.beta-confirmation-modal__panel {
    position: relative;
    display: grid;
    width: min(30rem, calc(100vw - 2rem));
    max-height: calc(100vh - 2rem);
    grid-template-rows: auto minmax(0, 1fr) auto;
    overflow: hidden;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18);
    outline: none;
}

.beta-confirmation-modal__panel header {
    padding: 28px 40px 12px;
}

.beta-confirmation-modal__panel h2 {
    color: #060606;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
}

.beta-confirmation-modal__body {
    overflow-y: auto;
    padding: 12px 40px;
    color: #060606;
    font-size: 16px;
    line-height: 1.45;
}

.beta-confirmation-modal__panel footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 40px 28px;
}

.beta-confirmation-modal__panel footer button {
    min-height: 36px;
    border-radius: 999px;
    padding: 8px 16px;
    font-size: 14px;
    line-height: 1.25;
    outline: none;
}

.beta-confirmation-modal__panel footer button:focus-visible {
    outline: 2px solid var(--main-color);
    outline-offset: 2px;
}

.beta-confirmation-modal__panel .is-cancel {
    color: #a7a7a7;
}

.beta-confirmation-modal__panel .is-confirm {
    color: #fff;
}

.beta-confirmation-modal__panel .is-danger {
    background: #ef4444;
}

.beta-confirmation-modal__panel .is-success {
    background: #22c55e;
}

.beta-confirmation-modal__panel .is-warning {
    background: #eab308;
}

.beta-confirmation-modal__panel .is-info {
    background: #3b82f6;
}

.beta-confirmation-modal__panel .is-main {
    background: var(--main-color);
}

@media (max-width: 520px) {
    .beta-confirmation-modal__panel header {
        padding: 24px 24px 10px;
    }

    .beta-confirmation-modal__body {
        padding: 10px 24px;
    }

    .beta-confirmation-modal__panel footer {
        padding: 12px 24px 24px;
    }
}
</style>
